import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Calendar, Tag } from "lucide-react";
import { loadBlogList } from "@/lib/content";
import { breadcrumbSchema } from "@/lib/schema";
import { Rise } from "@/components/motion/Reveal";
import useSEO from "@/hooks/useSEO";

function fmtDate(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
  } catch { return ""; }
}

export default function Blog() {
  const [posts, setPosts] = useState(null);

  useSEO({
    title: "Field Notes — ElectroMax Blog",
    description: "Straight-talk articles from Metro Vancouver electricians on EV chargers, panel upgrades, rebates, permits, and safety.",
    jsonld: breadcrumbSchema([
      { name: "Home", href: "/" },
      { name: "Blog", href: "/blog" },
    ]),
  });

  useEffect(() => {
    loadBlogList().then(setPosts).catch(() => setPosts([]));
  }, []);

  return (
    <div data-testid="blog-page" className="bg-[#0A0A0A] text-white min-h-screen">
      {/* Header */}
      <section className="relative pt-36 md:pt-44 pb-16 overflow-hidden grain">
        <div className="pointer-events-none absolute -top-40 -left-40 w-[520px] h-[520px] bg-[#E60000]/10 blur-[120px] rounded-full" />
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
          <div className="text-xs tracking-[0.24em] uppercase text-[#E60000] font-bold mb-5 inline-flex items-center gap-3">
            <span className="inline-block h-px w-8 bg-[#E60000]" />
            The Field Notes
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-5xl sm:text-6xl lg:text-[88px] leading-[0.95] tracking-tighter font-black max-w-4xl"
          >
            Notes from
            <br />
            <span className="text-white/40">the panel side.</span>
          </motion.h1>
          <p className="mt-6 max-w-2xl text-white/70 leading-relaxed text-base md:text-lg">
            Straight-talk articles from journeymen who&rsquo;ve pulled a lot of wire in
            a lot of Metro Vancouver homes. EV chargers, panel upgrades, rebates,
            safety — no filler, no upsell.
          </p>
        </div>
      </section>

      <section className="border-t border-[#262626] py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
          {posts === null && (
            <div className="text-white/50" data-testid="blog-loading">Loading posts…</div>
          )}
          {posts && posts.length === 0 && (
            <div className="text-white/50" data-testid="blog-empty">No posts published yet.</div>
          )}
          {posts && posts.length > 0 && (
            <>
              {/* Featured (first) */}
              <Rise>
                <Link
                  to={`/blog/${posts[0].slug}`}
                  data-testid={`blog-featured-${posts[0].slug}`}
                  className="group grid lg:grid-cols-12 gap-8 lg:gap-12 border-b border-[#262626] pb-14 mb-14 hover:opacity-95 transition-opacity"
                >
                  <div className="lg:col-span-7 relative overflow-hidden border border-[#262626]">
                    {posts[0].cover ? (
                      <img
                        src={posts[0].cover}
                        alt={posts[0].title}
                        className="w-full h-[280px] md:h-[440px] object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03]"
                      />
                    ) : (
                      <div className="w-full h-[280px] md:h-[440px] bg-[#141414]" />
                    )}
                    <div className="absolute top-4 left-4 bg-[#E60000] text-white text-[10px] tracking-[0.24em] uppercase font-bold px-3 py-2">
                      Latest
                    </div>
                  </div>
                  <div className="lg:col-span-5 flex flex-col justify-center">
                    <div className="flex items-center gap-4 text-[10px] tracking-[0.24em] uppercase text-white/50 mb-4">
                      <span className="inline-flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {fmtDate(posts[0].date)}</span>
                      {posts[0].author && <span>· {posts[0].author}</span>}
                    </div>
                    <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tighter font-black leading-[0.98]">
                      {posts[0].title}
                    </h2>
                    <p className="mt-5 text-white/70 leading-relaxed text-base">
                      {posts[0].excerpt}
                    </p>
                    <div className="mt-8 inline-flex items-center gap-2 text-[#E60000] font-bold tracking-wide text-sm group-hover:gap-3 transition-all">
                      Read the article <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
                    </div>
                  </div>
                </Link>
              </Rise>

              {/* Rest as grid */}
              <motion.div
                className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}
              >
                {posts.slice(1).map((p) => (
                  <motion.div
                    key={p.slug}
                    variants={{
                      hidden: { opacity: 0, y: 24 },
                      show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
                    }}
                  >
                    <Link
                      to={`/blog/${p.slug}`}
                      data-testid={`blog-card-${p.slug}`}
                      className="group flex flex-col border border-[#262626] bg-[#0F0F0F] hover:border-[#E60000] transition-colors h-full"
                    >
                      <div className="aspect-[16/10] overflow-hidden border-b border-[#262626]">
                        {p.cover ? (
                          <img src={p.cover} alt={`${p.title} — ElectroMax blog article`} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700" />
                        ) : (
                          <div className="w-full h-full bg-[#141414]" />
                        )}
                      </div>
                      <div className="p-5 md:p-6 flex flex-col grow">
                        <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 mb-3 inline-flex items-center gap-1.5">
                          <Calendar className="w-3 h-3" /> {fmtDate(p.date)}
                        </div>
                        <h3 className="font-display text-xl md:text-2xl tracking-tight font-bold leading-tight">
                          {p.title}
                        </h3>
                        <p className="mt-3 text-sm text-white/60 line-clamp-3 leading-relaxed">
                          {p.excerpt}
                        </p>
                        <div className="mt-auto pt-5 flex items-center justify-between">
                          {p.tags?.[0] && (
                            <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-[#E60000] font-bold">
                              <Tag className="w-3 h-3" /> {p.tags[0]}
                            </span>
                          )}
                          <ArrowUpRight className="w-4 h-4 text-white/40 group-hover:text-[#E60000] transition-colors" strokeWidth={2.5} />
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </motion.div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
